// TODO: Technical Debt, remove additional usage of Multi/Single Select dropdown and make it consistent throughout
import { OptionNameValuePair } from "@/src/lib/interfaces/types";
import { Check, ChevronDown, ChevronUp, Search, X } from "lucide-react";
import { ChangeEvent, useEffect, useRef, useState } from "react";
import { cn } from '../../../../lib/utils'

interface MultiSelectDropdownProps<T> {
    name: string;
    placeholder?: string;
    options: OptionNameValuePair<T>[];
    selectedValues: OptionNameValuePair<T>[] | undefined;
    onSelectionChange: (selectedValues: OptionNameValuePair<T>[]) => void;
    enableSearch?: boolean;
    containerClassName?: string;
    labelClassName?: string;
    disabled?: boolean;
    showCount?: boolean;
    footer?: React.ReactNode;
}

function MultiSelectDropdownV2<T>({
    name,
    placeholder,
    options,
    selectedValues,
    onSelectionChange,
    enableSearch = true,
    containerClassName,
    labelClassName,
    showCount = false,
    disabled = false,
    footer
}: MultiSelectDropdownProps<T>) {
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);
    const [visibleOptions, setVisibleOptions] = useState<OptionNameValuePair<T>[]>(options)

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    useEffect(() => {
        setVisibleOptions(options);
    }, [options])

    const toggleIsOpen = () => {
        if (disabled) return;
        setIsOpen(current => !current)
    }

    const filterOptions = (event: ChangeEvent<HTMLInputElement>) => {
        const filteredOptions = options.filter(option => option.name.toLowerCase().startsWith(event.target.value.toLowerCase()));
        setVisibleOptions(filteredOptions);
    }

    const toggleOption = (value: T) => {
        const hasSelectedValue = selectedValues?.find((v) => v.value === value);
        if (hasSelectedValue && selectedValues) {
            const updatedValues = selectedValues.filter((v) => v.value !== value);
            onSelectionChange(updatedValues);
        } else {
            const optionToAdd = options.find((o) => o.value === value);
            if (!optionToAdd) return;
            const updatedValues = [...(selectedValues || []), optionToAdd];
            onSelectionChange(updatedValues);
        }
    }

    return (
        <div ref={dropdownRef} className={cn("relative", containerClassName)}>
            <div
                className={cn("flex items-center justify-between w-full border-0 rounded cursor-pointer", labelClassName)}
                onClick={toggleIsOpen}
            >
                <span className="text-sm text-gray-500">Select {name}</span>
                <div className="flex items-center">
                    {showCount && selectedValues && selectedValues.length > 0 && (
                        <span className="ml-1.5 bg-blue-100 text-blue-800 text-xs font-medium rounded-full w-5 h-5 flex items-center justify-center">
                            {selectedValues.length}
                        </span>
                    )}
                    {!disabled && (
                        isOpen ? <ChevronUp className='ml-2 h-4 text-gray-500' /> : <ChevronDown className='ml-2 h-4 text-gray-500' />
                    )}
                </div>
            </div>
            {isOpen &&
                <div className="absolute left-0 top-full z-11 mt-1 w-full min-w-[180px] bg-white border border-gray-300 rounded-md shadow-overviewCard max-h-60 overflow-auto">
                    {enableSearch &&
                        <div className="sticky top-0 bg-white p-1">
                            <div className="relative">
                                <Search className="absolute left-2 top-1.5 h-4 w-4 text-gray-400" />
                                <input
                                    className="w-full pl-7 pr-3 py-1 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 text-sm"
                                    placeholder={placeholder || `Search ${name.toLocaleLowerCase()}`}
                                    type="text"
                                    onChange={filterOptions}
                                />
                            </div>
                        </div>
                    }
                    <div className="p-1 pb-2 space-y-1">
                        {visibleOptions.map((option => {
                            const isSelected = selectedValues && selectedValues.find((v) => v.value === option.value);
                            return (
                                <div 
                                    key={option.name} 
                                    className={cn(
                                        "rounded-md text-sm px-4 py-1.5 flex items-center cursor-pointer hover:bg-gray-100",
                                        isSelected && "bg-slate-200"
                                    )}
                                    onClick={() => { toggleOption(option.value) }}
                                >
                                    <div className={cn(
                                        "w-4 h-4 border rounded flex items-center justify-center mr-2",
                                        isSelected ? "bg-blue-500 border-blue-500" : "border-gray-300"
                                    )}>
                                        {isSelected && <Check className="h-3 w-3 text-white" />}
                                    </div>
                                    <span className="flex-1">{option.name}</span>
                                </div>
                            );
                        }))}
                    </div>
                    {footer && (
                        <div className="p-1 border-t border-gray-200">
                            {footer}
                        </div>
                    )}
                </div>
            }
        </div>
    )
}

export default MultiSelectDropdownV2;
